import { getUnitDefinition } from '@center-platform/units';
import type { MetricUnit } from 'types/metric';

type TranslateFn = (key: string) => string;

export interface FormatMetricValueInput {
  value: number;
  unit: MetricUnit;
  locale: string;
  t: TranslateFn;
}

export interface FormattedMetricValue {
  /** Compact form for a card ("$83M"); identical to `full` below 10 000. */
  display: string;
  /** Complete form for the accessible name ("$83,000,000.00"); never compact. */
  full: string;
}

const COMPACT_THRESHOLD = 10_000;

function buildFormatters(
  unit: MetricUnit,
  locale: string
): { compact: Intl.NumberFormat; full: Intl.NumberFormat; scale: number } {
  const make = (options: Intl.NumberFormatOptions, compact: boolean) =>
    new Intl.NumberFormat(locale, compact ? { ...options, notation: 'compact' } : options);

  switch (unit.family) {
    case 'currency': {
      if (unit.currencyCode) {
        const currency = { style: 'currency' as const, currency: unit.currencyCode };
        return {
          compact: make({ ...currency, maximumFractionDigits: 1 }, true),
          full: make(currency, false),
          scale: 1,
        };
      }
      const plain = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
      return {
        compact: make({ maximumFractionDigits: 1 }, true),
        full: make(plain, false),
        scale: 1,
      };
    }
    case 'percent': {
      // Values are stored as points (42 = 42%), so Intl is fed value / 100.
      const percent = { style: 'percent' as const, maximumFractionDigits: 1 };
      return { compact: make(percent, false), full: make(percent, false), scale: 100 };
    }
    case 'ratio': {
      const ratio = { signDisplay: 'exceptZero' as const, maximumFractionDigits: 2 };
      return { compact: make(ratio, true), full: make(ratio, false), scale: 1 };
    }
    case 'count':
      return {
        compact: make({ maximumFractionDigits: 0 }, true),
        full: make({ maximumFractionDigits: 0 }, false),
        scale: 1,
      };
    default:
      return {
        compact: make({ maximumFractionDigits: 2 }, true),
        full: make({ maximumFractionDigits: 2 }, false),
        scale: 1,
      };
  }
}

function withUnitSuffix(text: string, unit: MetricUnit, t: TranslateFn): string {
  if (unit.family !== 'physical' || !unit.unitCategory || !unit.defaultUnit) return text;
  const definition = getUnitDefinition(unit.defaultUnit, unit.unitCategory);
  return definition ? `${text} ${t(definition.abbrevKey)}` : text;
}

/**
 * Format a metric value from its metric block's unit. Never assumes a currency and never
 * coerces: a non-finite input is a caller bug and throws.
 */
export function formatMetricValue({
  value,
  unit,
  locale,
  t,
}: FormatMetricValueInput): FormattedMetricValue {
  if (!Number.isFinite(value)) {
    throw new TypeError(`formatMetricValue: value must be a finite number, received ${value}`);
  }
  const { compact, full, scale } = buildFormatters(unit, locale);
  const scaled = value / scale;
  const fullText = withUnitSuffix(full.format(scaled), unit, t);
  const useCompact = unit.family !== 'percent' && Math.abs(value) >= COMPACT_THRESHOLD;
  const displayText = useCompact ? withUnitSuffix(compact.format(scaled), unit, t) : fullText;
  return { display: displayText, full: fullText };
}
